/**
 * Antrean kirim pesan chat (Okt 2026, Fase 4A) — "kirim nanti" seperti Telegram.
 *
 * Masalah yang diatasi: di sinyal lemah, kirim pesan gagal lalu pesan dibuang
 * dan user harus mengetik ulang. Sekarang pesan teks yang gagal karena SINYAL
 * (tanpa respons, timeout, 408/429/502/503/504) disimpan di perangkat dan
 * dikirim ulang otomatis di latar belakang, urut, sampai berhasil.
 *
 * Prinsip:
 *  - Hanya pesan teks. Lampiran dan edit pesan tidak masuk antrean (file tidak
 *    disimpan di sini).
 *  - Urutan terjaga: per chat hanya pesan terlama yang dicoba lebih dulu.
 *  - Anti kirim ganda: respons yang hilang di sinyal lemah bisa berarti pesan
 *    sebenarnya sudah masuk. Sebelum mengirim ulang, daftar pesan chat dibaca
 *    dulu. Kalau sudah ada pesan baru (id tidak ada saat pesan diantre) dari
 *    pengirim yang sama dengan isi yang sama, pesan dianggap sudah terkirim.
 *  - Kesalahan permanen (4xx selain 408/429, mis. chat diarsipkan) menghapus
 *    pesan dari antrean dan memberi tahu layar.
 *  - Berjalan di tingkat aplikasi: tetap mengirim walau user pindah layar.
 *  - Disimpan di localStorage; hanya diproses untuk user yang sedang login.
 */

import axios from 'axios';
import { dropMatchedTemps } from '@/components/chat/chatText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'vaxa_chat_outbox_v1';
const MAX_ITEMS = 50;
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 hari
const BACKOFF_MS = [4000, 8000, 15000, 30000, 60000];
const POST_TIMEOUT_MS = 25000;
const PROBE_TIMEOUT_MS = 30000;
// Setelah sekian percobaan probe gagal, kirim saja (lebih baik dobel daripada hilang).
const MAX_PROBE_FAILS = 4;
// Pesan di server yang jauh lebih tua dari waktu kirim tidak boleh dianggap pasangan
// pesan antrean (mis. "ok" lama di chat panjang). Toleransi lebar karena zona waktu
// cap waktu server bisa berbeda.
const PROBE_AGE_TOLERANCE_MS = 15 * 60 * 60 * 1000;

let items = loadItems();
const subscribers = new Set();
let timer = null;
let running = false;
let started = false;

function loadItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* storage penuh / diblokir: antrean tetap jalan di memori */
  }
}

function emit(event) {
  subscribers.forEach((fn) => {
    try { fn(event); } catch { /* pendengar tidak boleh merusak antrean */ }
  });
}

export function currentUserId() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64)).user_id || null;
  } catch {
    return null;
  }
}

/** Apakah error ini karena sinyal (layak dicoba ulang)? */
export function isTransientSendError(err) {
  if (!err || !axios.isAxiosError(err)) return false;
  if (axios.isCancel(err) || err.code === 'ERR_CANCELED') return false;
  if (!err.response) return true; // tanpa respons: jaringan putus atau timeout
  return [408, 425, 429, 502, 503, 504].includes(err.response.status);
}

const mine = () => {
  const uid = currentUserId();
  return uid ? items.filter((i) => i.userId === uid) : [];
};

export function getPending(chatKey) {
  return mine().filter((i) => i.chatKey === chatKey).sort((a, b) => a.queuedAt - b.queuedAt);
}

export function hasPending(chatKey) {
  return getPending(chatKey).length > 0;
}

export function getPendingCount() {
  return mine().length;
}

// Layar yang sedang membuka chat tertentu (dipakai supaya pesan gagal permanen tetap diberi tahu
// ke user kalau layar chat-nya sudah ditutup).
const activeScreens = new Map();
export function markScreenActive(chatKey) {
  activeScreens.set(chatKey, (activeScreens.get(chatKey) || 0) + 1);
  return () => {
    const n = (activeScreens.get(chatKey) || 1) - 1;
    if (n <= 0) activeScreens.delete(chatKey); else activeScreens.set(chatKey, n);
  };
}
export function isScreenActive(chatKey) {
  return (activeScreens.get(chatKey) || 0) > 0;
}

export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/**
 * Masukkan pesan teks ke antrean.
 * spec: { id, chatKey, postPath, listPath, payload, tempMessage, knownIds, maybeLanded }
 */
export function adoptSend(spec) {
  const userId = currentUserId();
  if (!userId) return null;
  if (items.some((i) => i.id === spec.id)) return null;
  if (items.length >= MAX_ITEMS) return null;
  const sentAt = Date.parse(spec.tempMessage?.created_at) || Date.now();
  const item = {
    id: spec.id,
    userId,
    chatKey: spec.chatKey,
    postPath: spec.postPath,
    listPath: spec.listPath,
    payload: spec.payload,
    tempMessage: { ...spec.tempMessage, _outbox: true },
    sentAt,
    knownIds: Array.isArray(spec.knownIds) ? spec.knownIds.slice(-200) : [],
    maybeLanded: spec.maybeLanded !== false,
    queuedAt: Date.now(),
    attempts: 0,
    probeFails: 0,
    // Setelah timeout, server mungkin masih memproses kiriman asli: beri jeda sebelum cek.
    nextAt: Date.now() + (spec.maybeLanded === false ? 3000 : 10000),
  };
  items.push(item);
  persist();
  emit({ type: 'queued', item });
  schedule();
  return item;
}

function remove(id) {
  items = items.filter((i) => i.id !== id);
  persist();
}

function purgeExpired() {
  const now = Date.now();
  const expired = items.filter((i) => now - i.queuedAt > MAX_AGE_MS);
  if (!expired.length) return;
  items = items.filter((i) => now - i.queuedAt <= MAX_AGE_MS);
  persist();
  expired.forEach((item) => emit({ type: 'failed', item, reason: 'expired' }));
}

function listFrom(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.messages)) return data.messages;
  return [];
}

// Apakah pesan ini sudah ada di server? true / false / null (tidak bisa dipastikan)
async function alreadyDelivered(item, headers) {
  if (!item.maybeLanded) return false;
  try {
    const res = await axios.get(`${API}${item.listPath}`, { headers, timeout: PROBE_TIMEOUT_MS });
    const known = new Set(item.knownIds || []);
    const me = item.userId;
    const candidates = listFrom(res.data).filter((m) => {
      if (!m || known.has(m.message_id)) return false;
      const ts = Date.parse(m.timestamp || m.created_at);
      if (item.sentAt && !Number.isNaN(ts) && ts < item.sentAt - PROBE_AGE_TOLERANCE_MS) return false;
      const sender = m.sender_user_id ?? m.sender_id ?? null;
      return sender == null || sender === me;
    });
    const unmatched = dropMatchedTemps([item.tempMessage], candidates);
    if (unmatched.length === 0) {
      // Catat pesan yang cocok supaya pesan antrean lain dengan isi sama tidak ikut cocok.
      const used = new Set();
      const pool = [...candidates];
      const text = (m) => String(m?.content ?? m?.message_text ?? m?.message ?? '').trim();
      const idx = pool.findIndex((m) => text(m) === text(item.tempMessage));
      if (idx !== -1 && pool[idx].message_id) used.add(pool[idx].message_id);
      if (used.size) {
        items.forEach((other) => {
          if (other.id !== item.id && other.chatKey === item.chatKey) {
            other.knownIds = [...(other.knownIds || []), ...used].slice(-200);
          }
        });
      }
      return true;
    }
    return false;
  } catch {
    return null;
  }
}

function backoff(attempts) {
  return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];
}

async function attempt(item) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const landed = await alreadyDelivered(item, headers);
  if (landed === true) {
    remove(item.id);
    emit({ type: 'delivered', item, viaProbe: true });
    return;
  }
  if (landed === null && item.probeFails < MAX_PROBE_FAILS) {
    // Belum bisa memastikan: tunggu dulu, jangan kirim.
    item.probeFails += 1;
    item.attempts += 1;
    item.nextAt = Date.now() + backoff(item.attempts);
    persist();
    return;
  }

  try {
    await axios.post(`${API}${item.postPath}`, item.payload, { headers, timeout: POST_TIMEOUT_MS });
    remove(item.id);
    emit({ type: 'delivered', item });
  } catch (err) {
    if (isTransientSendError(err)) {
      item.attempts += 1;
      item.maybeLanded = true; // percobaan ini mungkin sudah masuk
      item.nextAt = Date.now() + backoff(item.attempts);
      persist();
      emit({ type: 'retry', item });
      return;
    }
    // 401 ditangani pencegat login (refresh token); error tanpa respons lain (mis. dibatalkan
    // saat logout) juga tidak boleh membuang pesan: tahan dan coba lagi nanti.
    if (!err?.response || err.response.status === 401) {
      item.attempts += 1;
      item.nextAt = Date.now() + backoff(item.attempts);
      persist();
      return;
    }
    remove(item.id);
    emit({
      type: 'failed',
      item,
      status: err?.response?.status || null,
      detail: err?.response?.data?.detail || null,
    });
  }
}

function pickDueHead() {
  const now = Date.now();
  const heads = [];
  const seen = new Set();
  mine()
    .sort((a, b) => a.queuedAt - b.queuedAt)
    .forEach((i) => {
      if (seen.has(i.chatKey)) return;
      seen.add(i.chatKey);
      heads.push(i);
    });
  return heads.filter((h) => h.nextAt <= now).sort((a, b) => a.queuedAt - b.queuedAt)[0] || null;
}

async function tick() {
  timer = null;
  if (running) return;
  running = true;
  try {
    purgeExpired();
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (!currentUserId()) return;
    for (let guard = 0; guard < 200; guard += 1) {
      const head = pickDueHead();
      if (!head) break;
      await attempt(head);
    }
  } finally {
    running = false;
    schedule();
  }
}

function schedule() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (running) return;
  const pending = mine();
  if (!pending.length) return;
  const next = Math.min(...pending.map((i) => i.nextAt));
  timer = setTimeout(tick, Math.max(500, next - Date.now()));
}

/** Percepat: coba segera (dipanggil saat jaringan kembali / app dibuka lagi). */
export function kick() {
  mine().forEach((i) => {
    if (i.nextAt > Date.now()) i.nextAt = Date.now();
  });
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  timer = setTimeout(tick, 300);
}

/** Pastikan jadwal berjalan (mis. setelah logout lalu login lagi). */
export function resume() {
  if (!timer && !running && mine().length) schedule();
}

/** Mulai pemroses antrean (sekali, di tingkat aplikasi). */
export function startOutboxRunner() {
  if (started) return () => {};
  started = true;
  const onOnline = () => kick();
  const onVisible = () => { if (!document.hidden) kick(); };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  schedule();
  return () => {
    started = false;
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    if (timer) clearTimeout(timer);
    timer = null;
  };
}

// Hanya untuk pengujian.
export const __testing = {
  reset() {
    items = [];
    persist();
    if (timer) clearTimeout(timer);
    timer = null;
    running = false;
  },
  items: () => items,
  tick,
};
